import { Element, NO_INTERACTIVITY } from '@thive/canvas';
import { JustInTimeRoutingConfig, PointOfInterest, Pose, Rectangle } from '@thive/fddm-types';
import { MathUtils, Vector2, Vector3 } from 'three';

import { theme } from '@modules/common/theme';
import { FDDM_TAG, INTERACTIVITY_OFF } from '@/modules/floorplanDesign/constants';
import { AREAS_COLORS_MAP, modifyColor } from '@/modules/floorplanDesign/mappers/colors';

import { Z_INDEX_MAP } from '@/modules/canvas/constants/elements';
import { elementFactory } from '@/modules/canvas/factories';
import { calcJitPointSize } from '@/modules/canvas';
import { METERS_TO_MILLIMETERS_FACTOR } from '../factories/element';

const POSE_BACKGROUND_COLOR = theme.palette.splines;
const POSE_TRIANGLE_WIDTH = 160;
const POSE_TRIANGLE_HEIGHT = 140;
const POSE_ARROW_COLOR = theme.palette.splines;
const POSE_ARROW_STROKE_WIDTH = 0.02;
const POSE_ARROW_EDGE_OFFSET = POSE_ARROW_STROKE_WIDTH / 2;
const POSE_ARROW_SIDE_OFFSET = POSE_ARROW_STROKE_WIDTH / 2;
const POSE_ARROW_ARM_ANGLE_DEG = 70;
const JIT_POINT_COLOR_MODIFIER = '4b';
const POI_RECTANGLE_DIRECTION_PRECISION = 1_000_000;

const toCanvasMillimeters = (value: number): number =>
  Math.round(value * METERS_TO_MILLIMETERS_FACTOR);

const poisMeanPosition = (pois: PointOfInterest[]): Vector2 | undefined => {
  if (pois.length === 0) {
    return undefined;
  }

  return pois
    .reduce((avg: Vector2, poi) => avg.add(new Vector2(poi.pose.x, poi.pose.y)), new Vector2(0, 0))
    .divideScalar(pois.length);
};

const rotateVector = (vector: Vector2, angle: number): Vector2 =>
  new Vector2(
    vector.x * Math.cos(angle) - vector.y * Math.sin(angle),
    vector.x * Math.sin(angle) + vector.y * Math.cos(angle),
  );

const getArrowHeight = (arrowHalfWidth: number): number =>
  arrowHalfWidth / Math.tan(MathUtils.degToRad(POSE_ARROW_ARM_ANGLE_DEG));

const getPoiArrowGeometry = (
  rectangle: Rectangle,
  pose: Pose,
): {
  edgeCenter: Vector2;
  inwardAxis: Vector2;
  edgeAxis: Vector2;
} => {
  const center = new Vector2(rectangle.centerX, rectangle.centerY);
  const longitudinalAxis = new Vector2(Math.cos(rectangle.angle), Math.sin(rectangle.angle));
  const poseDirection = new Vector2(Math.cos(pose.angle), Math.sin(pose.angle));

  const endA = center.clone().add(longitudinalAxis.clone().multiplyScalar(rectangle.length / 2));
  const endB = center.clone().sub(longitudinalAxis.clone().multiplyScalar(rectangle.length / 2));
  const edgeCenter = longitudinalAxis.dot(poseDirection) >= 0 ? endA : endB;

  const inwardAxis = center.clone().sub(edgeCenter).normalize();
  const edgeAxis = new Vector2(-inwardAxis.y, inwardAxis.x);

  return { edgeCenter, inwardAxis, edgeAxis };
};

const toPoseLocalMm = (worldPoint: Vector2, pose: Pose): Vector2 => {
  const poseRotation = pose.angle - Math.PI / 2;
  const offsetFromPose = worldPoint.clone().sub(new Vector2(pose.x, pose.y));

  return rotateVector(offsetFromPose, -poseRotation).multiplyScalar(METERS_TO_MILLIMETERS_FACTOR);
};

const createPoseElements = (poi: PointOfInterest, visible = true): Element[] => {
  const poseGroup = elementFactory.buildNoneElement({
    id: `${poi.id}-pose`,
    position: new Vector3(poi.pose.x, poi.pose.y, Z_INDEX_MAP[FDDM_TAG.POSE]),
    rotation: new Vector3(0, 0, MathUtils.radToDeg(poi.pose.angle) - 90),
    interactivity: INTERACTIVITY_OFF,
    tags: [FDDM_TAG.POSE, FDDM_TAG.LAYER_FLOORPLAN],
    visible,
  });

  const poseBackground = elementFactory.buildNgonElement({
    id: `${poi.id}-pose-bg`,
    parentId: poseGroup.id,
    color: POSE_BACKGROUND_COLOR,
    position: new Vector3(),
    rotation: new Vector3(),
    points: createPoseTrianglePoints(POSE_TRIANGLE_WIDTH, POSE_TRIANGLE_HEIGHT),
    interactivity: INTERACTIVITY_OFF,
    tags: [FDDM_TAG.POSE, FDDM_TAG.LAYER_FLOORPLAN],
    visible,
  });

  const { edgeCenter, inwardAxis, edgeAxis } = getPoiArrowGeometry(poi.rectangle, poi.pose);
  const arrowHalfWidth = Math.max(poi.rectangle.width / 2 - POSE_ARROW_SIDE_OFFSET, 0);
  const arrowHeight = getArrowHeight(arrowHalfWidth);

  const arrowApex = edgeCenter
    .clone()
    .add(inwardAxis.clone().multiplyScalar(POSE_ARROW_EDGE_OFFSET));
  const arrowBaseCenter = arrowApex.clone().add(inwardAxis.clone().multiplyScalar(arrowHeight));
  const arrowLeftBase = arrowBaseCenter
    .clone()
    .sub(edgeAxis.clone().multiplyScalar(arrowHalfWidth));
  const arrowRightBase = arrowBaseCenter
    .clone()
    .add(edgeAxis.clone().multiplyScalar(arrowHalfWidth));

  const localArrowApex = toPoseLocalMm(arrowApex, poi.pose);
  const localArrowLeftBase = toPoseLocalMm(arrowLeftBase, poi.pose);
  const localArrowRightBase = toPoseLocalMm(arrowRightBase, poi.pose);
  const arrowThickness = POSE_ARROW_STROKE_WIDTH * METERS_TO_MILLIMETERS_FACTOR;

  const poseArrowLeft = elementFactory.buildNgonElement({
    id: `${poi.id}-pose-needle-left`,
    parentId: poseGroup.id,
    color: POSE_ARROW_COLOR,
    position: new Vector3(),
    rotation: new Vector3(),
    points: createPoseArrowArmPoints(localArrowLeftBase, localArrowApex, arrowThickness),
    interactivity: INTERACTIVITY_OFF,
    tags: [FDDM_TAG.POSE, FDDM_TAG.LAYER_FLOORPLAN],
    visible,
  });

  const poseArrowRight = elementFactory.buildNgonElement({
    id: `${poi.id}-pose-needle-right`,
    parentId: poseGroup.id,
    color: POSE_ARROW_COLOR,
    position: new Vector3(),
    rotation: new Vector3(),
    points: createPoseArrowArmPoints(localArrowApex, localArrowRightBase, arrowThickness),
    interactivity: INTERACTIVITY_OFF,
    tags: [FDDM_TAG.POSE, FDDM_TAG.LAYER_FLOORPLAN],
    visible,
  });

  return [poseGroup, poseBackground, poseArrowLeft, poseArrowRight];
};

const createJitRoutingPointElement = (id: string, center: Vector2, zoom: number): Element => {
  const size = calcJitPointSize(zoom);

  return elementFactory.buildRectangleElement({
    id,
    rectangle: {
      centerX: center.x,
      centerY: center.y,
      width: size,
      length: size,
      angle: MathUtils.degToRad(45),
    },
    z: Z_INDEX_MAP[FDDM_TAG.JIT_POINT],
    color: modifyColor(AREAS_COLORS_MAP.JIT_POINT, false, JIT_POINT_COLOR_MODIFIER),
    interactivity: NO_INTERACTIVITY,
    tags: [FDDM_TAG.JIT_POINT],
  });
};

const groupPoisByJitRoutingConfigId = (pois: PointOfInterest[]): Map<string, PointOfInterest[]> => {
  const poisByJitRoutingConfigId = new Map<string, PointOfInterest[]>();

  pois.forEach((poi) => {
    const jitRoutingConfigIds = new Set(poi.jitRoutingConfigIds ?? []);

    jitRoutingConfigIds.forEach((jitRoutingConfigId) => {
      const relatedPois = poisByJitRoutingConfigId.get(jitRoutingConfigId) ?? [];
      relatedPois.push(poi);
      poisByJitRoutingConfigId.set(jitRoutingConfigId, relatedPois);
    });
  });

  return poisByJitRoutingConfigId;
};

const getGeometryKey = (rectangle: Rectangle, pose: Pose): string => {
  // Doubling the angle makes rectangles rotated by 180 degrees share one direction.
  const doubledAngle = rectangle.angle * 2;

  return [
    toCanvasMillimeters(rectangle.centerX),
    toCanvasMillimeters(rectangle.centerY),
    toCanvasMillimeters(rectangle.length),
    toCanvasMillimeters(rectangle.width),
    Math.round(Math.cos(doubledAngle) * POI_RECTANGLE_DIRECTION_PRECISION),
    Math.round(Math.sin(doubledAngle) * POI_RECTANGLE_DIRECTION_PRECISION),
    toCanvasMillimeters(pose.x),
    toCanvasMillimeters(pose.y),
    Math.round(Math.cos(pose.angle) * POI_RECTANGLE_DIRECTION_PRECISION),
    Math.round(Math.sin(pose.angle) * POI_RECTANGLE_DIRECTION_PRECISION),
  ].join(':');
};

const getPoisWithUniqueGeometry = (pois: readonly PointOfInterest[]): PointOfInterest[] => {
  const geometryKeys = new Set<string>();

  return pois.filter((poi) => {
    const geometryKey = getGeometryKey(poi.rectangle, poi.pose);

    if (geometryKeys.has(geometryKey)) return false;

    geometryKeys.add(geometryKey);
    return true;
  });
};

export const poiMapper = (
  pois: PointOfInterest[],
  jitRoutingPoints: JustInTimeRoutingConfig[],
  zoom: number,
  boxVisible: boolean,
  poseVisible: boolean,
): Element[] => {
  const poisWithUniqueGeometry = getPoisWithUniqueGeometry(pois);

  // Draw one background for POIs with the same visual rectangle. The element keeps a real POI id.
  const poiElements = poisWithUniqueGeometry.map((pointOfInterest) =>
    elementFactory.buildRectangleElement({
      id: pointOfInterest.id,
      rectangle: pointOfInterest.rectangle,
      z: Z_INDEX_MAP[FDDM_TAG.POINT_OF_INTEREST],
      color: modifyColor(theme.palette.areas.laneColor, false, '75'),
      interactivity: INTERACTIVITY_OFF,
      tags: [FDDM_TAG.POINT_OF_INTEREST, FDDM_TAG.LAYER_FLOORPLAN],
      visible: boxVisible,
    }),
  );

  // Preserve the pose belonging to every POI as well.
  const poseElements = poisWithUniqueGeometry.flatMap((poi) =>
    createPoseElements(poi, poseVisible),
  );

  const poisByJitRoutingConfigId = groupPoisByJitRoutingConfigId(pois);
  const jitRoutingPointElements = jitRoutingPoints.flatMap((jitRoutingPoint) => {
    const relatedPois = poisByJitRoutingConfigId.get(jitRoutingPoint.id) ?? [];
    const center = poisMeanPosition(relatedPois);

    if (!center) return [];

    return [createJitRoutingPointElement(jitRoutingPoint.id, center, zoom)];
  });

  return [...poiElements, ...poseElements, ...jitRoutingPointElements];
};

const createPoseTrianglePoints = (width: number, height: number): Vector2[] => [
  new Vector2(0, (height * 2) / 3),
  new Vector2(-width / 2, -height / 3),
  new Vector2(width / 2, -height / 3),
];

const createPoseArrowArmPoints = (start: Vector2, end: Vector2, thickness: number): Vector2[] => {
  const direction = end.clone().sub(start);

  if (direction.lengthSq() <= Number.EPSILON) {
    return [start.clone(), end.clone(), end.clone(), start.clone()];
  }

  const normal = new Vector2(-direction.y, direction.x).normalize().multiplyScalar(thickness / 2);

  return [
    start.clone().add(normal),
    end.clone().add(normal),
    end.clone().sub(normal),
    start.clone().sub(normal),
  ];
};
